import queryString from "query-string";
import * as Sentry from "@sentry/react";

import { BaseApi } from "./base";

import accountInformationResponse from "../fixtures/accountInformationResponse.json";
import createMatchProfileResponse from "../fixtures/createMatchProfileResponse.json";
import offlineCompanyResponse from "../fixtures/offlineCompanyResponse.json";
import companyMatchProfiles from "../fixtures/companyMatchProfiles.json";
import pagedCompanyHistory from "../fixtures/pagedCompanyHistory.json";
import pagedBlockedDomains from "../fixtures/pagedBlockedDomains.json";
import websiteStatusResponse from "../fixtures/websiteStatusResponse.json";
import openApiSchema from "../fixtures/openApiSchema.json";
import accountSearchStats from "../fixtures/accountSearchStats.json";
import domainFinderResponse from "../fixtures/domainFinderResponse.json";
import monitoringEnrollment from "../fixtures/monitoringEnrollment.json";
import monitoringEnrollments from "../fixtures/monitoringEnrollments.json";
import APIBlockedDomainInput from "../types/APIBlockedDomainInput";
import APIAccountSearchSummary from "../types/APIAccountSearchSummary";
import matchProfileConstants from "../fixtures/matchProfileConstants.json";
import { dataUrlToBlob } from "../utilities/testing";
import APISearchQuery from "../types/APISearchQuery";
import APICompanyMatchProfile from "../types/APICompanyMatchProfile";
import APIAccountInfo from "../types/APIAccountInfo";
import APICompany from "../types/APICompany";
import APICompanyMatchProfileConstants from "../types/APICompanyMatchProfileConstants";
import {
  APIMonitoringAnomaly,
  APIAnomalyFilterBy,
  APIAnomalyOrderBy,
  APIAnomalyResolutionType,
  APIAnomalyStatus,
  APIAnomalyUser,
} from "../types/APIAnomaly";
import {
  APIMonitoringEnrollment,
  APIMonitoringEnrollmentFilterBy,
  APIMonitoringEnrollmentMonitorConfiguration,
  APIMonitoringEnrollmentOrderBy,
  APIMonitorStatusType,
  APIMonitorType,
} from "../types/APIMonitoringEnrollment";
import APIDecisionFeedback from "../types/APIDecisionFeedback";
import { APIDataExport, APIDataExportRequest } from "../types/APIDataExport";
import { getBase64FileContents } from "../utilities/getBase64FileContents";
import { APIDataImportRequest } from "../types/APIDataImport";
import { APIBusinessSearch } from "../types/APIMCCSearch";
import { APIFraudSearch } from "../types/APIFraudSearch";
import APICompanyLookup from "../types/APICompanyLookup";
import type {
  APIPaginated,
  APIPaginatedWithPrevAndNextLinks,
} from "../types/APIPaginated";
import APIDeepSearch from "../types/APIDeepSearch";
import { APIRiskDefinition } from "../types/APIRiskDefinition";

const screenshot = require("../fixtures/screenshot.png");

export class TrueBizApi extends BaseApi {
  rootUrl = `${process.env.REACT_APP_TRUEBIZ_API_HOSTNAME}`;

  domainLookup(query: APISearchQuery) {
    return this.authenticated()
      .json()
      .stubbedWith(offlineCompanyResponse, { delayMs: 5_000 })
      .post(`${this.rootUrl}/company/search`, query) as Promise<APICompany>;
  }

  schemaLookup() {
    return this.anonymous()
      .json()
      .stubbedWith(openApiSchema, { delayMs: 250 })
      .get(`${this.rootUrl}/openapi.json`);
  }

  accountLookup() {
    return this.authenticated()
      .json()
      .stubbedWith(accountInformationResponse, { delayMs: 1_000 })
      .get(`${this.rootUrl}/account/`) as Promise<APIAccountInfo>;
  }

  webhookLookup(authUrl: string) {
    // This will return a non-response, which should trigger an alert in the UI.
    return this.authenticated()
      .json()
      .stubbedWith({}, { delayMs: 250 })
      .get(authUrl) as { dashboard_url?: string };
  }

  getDomainFinder(
    email: string | null,
    phone: string | null,
    businessName: string | null,
    addressLine1: string | null,
    addressLine2: string | null,
    city: string | null,
    stateProvince: string | null,
    postalCode: string | null,
    country: string | null
  ) {
    const queryParams = new URLSearchParams({
      email: String(email),
      phone: String(phone),
      business_name: String(businessName),
      address_line_1: String(addressLine1),
      address_line_2: String(addressLine2),
      city: String(city),
      state_province: String(stateProvince),
      postal_code: String(postalCode),
      country: String(country),
    });

    return this.authenticated()
      .json()
      .stubbedWith(domainFinderResponse)
      .get(`${this.rootUrl}/domain_finder/?${queryParams.toString()}`);
  }

  getCompanyLookups(
    searchTerm: string,
    minDate: string | null,
    maxDate: string | null,
    offset: number,
    limit: number
  ) {
    const queryParams = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
      ...(minDate ? { min_datetime: minDate } : {}),
      ...(maxDate ? { max_datetime: maxDate } : {}),
      ...(searchTerm ? { search_term: searchTerm } : {}),
    });

    return this.authenticated()
      .json()
      .stubbedWith(pagedCompanyHistory as APIPaginated<APICompanyLookup>)
      .get(
        `${this.rootUrl}/history/company?${queryParams.toString()}`
      ) as Promise<APIPaginated<APICompanyLookup>>;
  }

  getCompanyLookup({
    searchId,
    domain,
  }: {
    searchId: UUID | string;
    domain?: string | null | undefined;
  }) {
    if (searchId === "most_recent" && domain) {
      return this.authenticated()
        .json()
        .stubbedWith(pagedCompanyHistory.items[0] as APICompanyLookup)
        .get(
          `${this.rootUrl}/history/company/_meta/most_recent_search_for/${domain}`
        ) as Promise<APICompanyLookup>;
    }

    return this.authenticated()
      .json()
      .stubbedWith(pagedCompanyHistory.items[0] as APICompanyLookup)
      .get(
        `${this.rootUrl}/history/company/${searchId}`
      ) as Promise<APICompanyLookup>;
  }

  getCompanyLookupPdfSummary(requestId: string) {
    return this.authenticated()
      .untyped()
      .stubbedWith(() => dataUrlToBlob(screenshot), { type: "blob" }) // TODO: create empty pdf fixture
      .get(`${this.rootUrl}/pdf_summary/${requestId}`, {
        unwrapResponse: false,
      })
      .then(async (res: Response) => {
        if (res.status !== 200) {
          return { status: res.status, blob: null };
        }

        const blob = await res.blob();

        return {
          status: res.status,
          blob: blob || null,
        };
      });
  }

  submitCompanyLookupDecisionFeedback(
    searchId: UUID,
    payload: APIDecisionFeedback["feedback"]
  ) {
    return this.authenticated()
      .json()
      .stubbedWith({})
      .post(
        `${this.rootUrl}/history/company/${searchId}/_meta/decision_feedback`,
        payload
      ) as Promise<APIDecisionFeedback>;
  }

  getPassfortCompanyLookup(
    requestId: string,
    passfortParameters: URLSearchParams | null
  ) {
    const url = new URL(
      `${this.rootUrl}/passfort/company/${requestId}?` + passfortParameters
    );

    return this.anonymous()
      .json()
      .stubbedWith(pagedCompanyHistory.items[0])
      .get(url);
  }

  getAuthRelayCompanyLookup(
    requestId: string,
    relayParameters: URLSearchParams | null
  ) {
    const url = new URL(
      `${this.rootUrl}/relay/company/${requestId}?` + relayParameters
    );

    return this.anonymous()
      .json()
      .stubbedWith(pagedCompanyHistory.items[0])
      .get(url);
  }

  getScreenshot(
    src: string,
    signal: AbortSignal
  ): Promise<{ status: number; url: string | null }> {
    return this.authenticated()
      .untyped()
      .stubbedWith(() => dataUrlToBlob(screenshot), { type: "blob" })
      .get(src, { unwrapResponse: false, fetchOptions: { signal } })
      .then(async (res: Response) => {
        if (res.status !== 200) {
          return { status: res.status, url: null };
        }

        const blob = await res.blob();

        return {
          status: res.status,
          url: blob ? window.URL.createObjectURL(blob) : null,
        };
      });
  }

  getPassfortScreenshot(
    src: string,
    passfortParameters: URLSearchParams | null,
    passfortRequestId: string | null,
    signal: AbortSignal
  ): Promise<{ status: number; url: string | null }> {
    const url = new URL(src);
    if (!url.pathname.toString().startsWith("/api/v1/passfort/")) {
      url.pathname = url.pathname.replace("/api/v1/", "/api/v1/passfort/");
    }
    url.pathname += `/${passfortRequestId}`;
    url.search = new URLSearchParams({
      ...Object.fromEntries(url.searchParams),
      ...(passfortParameters ? Object.fromEntries(passfortParameters) : {}),
    }).toString();

    return this.anonymous()
      .untyped()
      .stubbedWith(() => dataUrlToBlob(screenshot), { type: "blob" })
      .get(url, { unwrapResponse: false, fetchOptions: { signal } })
      .then(async (res: Response) => {
        if (res.status !== 200) {
          return { status: res.status, url: null };
        }

        const blob = await res.blob();

        return {
          status: res.status,
          url: blob ? window.URL.createObjectURL(blob) : null,
        };
      });
  }

  getRelayScreenshot(
    src: string,
    relayParameters: URLSearchParams | null,
    relayRequestId: string | null,
    signal: AbortSignal
  ): Promise<{ status: number; url: string | null }> {
    const url = new URL(src);
    if (!url.pathname.toString().startsWith("/api/v1/relay/")) {
      url.pathname = url.pathname.replace("/api/v1/", "/api/v1/relay/");
    }
    url.pathname += `/${relayRequestId}`;
    url.search = new URLSearchParams({
      ...Object.fromEntries(url.searchParams),
      ...(relayParameters ? Object.fromEntries(relayParameters) : {}),
    }).toString();

    return this.anonymous()
      .untyped()
      .stubbedWith(() => dataUrlToBlob(screenshot), { type: "blob" })
      .get(url, { unwrapResponse: false, fetchOptions: { signal } })
      .then(async (res: Response) => {
        if (res.status !== 200) {
          return { status: res.status, url: null };
        }

        const blob = await res.blob();

        return {
          status: res.status,
          url: blob ? window.URL.createObjectURL(blob) : null,
        };
      });
  }

  getBlockedDomains(offset: number, limit: number) {
    const queryParams = new URLSearchParams({
      offset: String(offset),
      limit: String(limit),
    });

    const url = `${this.rootUrl}/company/block?${queryParams.toString()}`;

    return this.authenticated()
      .json()
      .stubbedWith(pagedBlockedDomains)
      .get(url);
  }

  deleteBlockedDomain(domain: string) {
    return this.authenticated()
      .json()
      .stubbedWith({ message: `Block deleted for ${domain}.` })
      .delete(`${this.rootUrl}/company/block`, { domain: domain });
  }

  createBlockedDomain(formData: APIBlockedDomainInput) {
    return this.authenticated()
      .json()
      .stubbedWith({ message: `Block created for ${formData.domain}.` })
      .post(`${this.rootUrl}/company/block`, formData);
  }

  getAnomalies({
    limit,
    offset = 0,
    orderBy = [],
    filterBy = [],
    search = "",
  }: {
    limit?: number;
    offset?: number;
    orderBy?: APIAnomalyOrderBy[];
    filterBy?: APIAnomalyFilterBy[];
    search?: string;
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }

            // we need a special empty array value to differentiate from None
            const qsValue = !Array.isArray(value)
              ? value
              : value.length === 0
              ? ["[]"]
              : value;

            if (operator === "in") {
              return [column, qsValue]; // in is now implied
            }

            return [`${column}_${operator}`, qsValue];
          })
        ),
        ...(search ? { search } : {}),
        ...(limit ? { limit } : {}),
        ...(offset ? { offset } : {}),
        ...(orderBy ? { order_by: orderBy } : {}),
      },
      {
        arrayFormat: "none", // repeated keys
        encode: true, // make sure things like emails get % encoded
      }
    );

    type PaginatedAnomalies = APIPaginatedWithPrevAndNextLinks<
      APIMonitoringAnomaly,
      "anomalies"
    >;

    return this.authenticated()
      .json()
      .stubbedWith({
        anomalies: [],
        next_anomalies_link: null,
        prev_anomalies_link: null,
        count: 0,
        limit: 0,
        offset: 0,
      })
      .get(
        `${this.rootUrl}/monitoring/_anomalies?${qs}`
      ) as Promise<PaginatedAnomalies>;
  }

  getAnomaly(anomalyId: UUID) {
    return this.authenticated()
      .json()
      .stubbedWith({})
      .get(
        `${this.rootUrl}/monitoring/_anomalies/${anomalyId}`
      ) as Promise<APIMonitoringAnomaly>;
  }

  getAnomaliesMetaUniqueValuesForDomain({
    filterBy = [],
  }: {
    filterBy?: APIAnomalyFilterBy[];
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }
            if (operator === "in") {
              return [column, value];
            }
            return [`${column}_${operator}`, value];
          })
        ),
      },
      {
        arrayFormat: "none", // repeated keys,
        encode: true, // make sure things like emails get % encoded
      }
    );
    return this.authenticated()
      .json()
      .stubbedWith([])
      .get(
        `${this.rootUrl}/monitoring/_anomalies/_meta/unique_values_for/domain?${qs}`
      ) as Promise<string[]>;
  }

  getAnomaliesMetaUniqueValuesForStatus({
    filterBy = [],
  }: {
    filterBy?: APIAnomalyFilterBy[];
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }
            if (operator === "in") {
              return [column, value];
            }
            return [`${column}_${operator}`, value];
          })
        ),
      },
      {
        arrayFormat: "none", // repeated keys,
        encode: true, // make sure things like emails get % encoded
      }
    );
    return this.authenticated()
      .json()
      .stubbedWith([])
      .get(
        `${this.rootUrl}/monitoring/_anomalies/_meta/unique_values_for/status?${qs}`
      ) as Promise<APIAnomalyStatus[]>;
  }

  getAnomaliesMetaUniqueValuesForAlerts({
    filterBy = [],
  }: {
    filterBy?: APIAnomalyFilterBy[];
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }
            if (operator === "in") {
              return [column, value];
            }
            return [`${column}_${operator}`, value];
          })
        ),
      },
      {
        arrayFormat: "none", // repeated keys,
        encode: true, // make sure things like emails get % encoded
      }
    );
    return this.authenticated()
      .json()
      .stubbedWith([])
      .get(
        `${this.rootUrl}/monitoring/_anomalies/_meta/unique_values_for/alerts?${qs}`
      ) as Promise<{ type: string; display: string }[]>;
  }

  getAnomaliesMetaAssignableUsers() {
    return this.authenticated()
      .json()
      .stubbedWith([
        {
          id: "00000000-0000-0000-0000-000000000000",
          email: "schmee@truebiz.io",
          full_name: null,
        },
      ])
      .get(
        `${this.rootUrl}/monitoring/_anomalies/_meta/assignable_users`
      ) as Promise<APIAnomalyUser[]>;
  }

  anomaliesMetaBulkAssignToUsers(
    anomalyIds: UUID[],
    assignedToId: UUID | null | undefined
  ) {
    return this.authenticated()
      .json()
      .stubbedWith([])
      .put(`${this.rootUrl}/monitoring/_anomalies/_meta/bulk/assign_to_user`, {
        anomaly_ids: anomalyIds,
        assigned_to_id: assignedToId,
      });
  }

  async initiateMonitoringEnrollmentsImport({
    uploadFile,
    emailOnSuccess,
    impersonatorUserId,
  }: {
    uploadFile: File;
    emailOnSuccess: boolean;
    impersonatorUserId: UUID | null;
  }) {
    const uploadFileName = uploadFile.name;
    const uploadFileMimeType = uploadFile.type;
    const uploadFileBase64Encoded = await getBase64FileContents(uploadFile);

    return this.authenticated()
      .json()
      .stubbedWith({
        id: "00000000-0000-0000-0000-000000000000",
        retrieval_url: "",
      })
      .post(`${this.rootUrl}/data_import/monitoring_enrollment`, {
        upload_file_base64_encoded: uploadFileBase64Encoded,
        upload_file_mime_type: uploadFileMimeType || null,
        upload_file_name: uploadFileName || null,
        email_on_success: emailOnSuccess,
        impersonator_user_id: impersonatorUserId,
      }) as Promise<APIDataImportRequest>;
  }

  initiateMonitoringAnomaliesExport({
    emailOnSuccess,
    impersonatorUserId,
  }: {
    emailOnSuccess: boolean;
    impersonatorUserId: UUID | null;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        id: "00000000-0000-0000-0000-000000000000",
        retrieval_url: "",
      })
      .post(`${this.rootUrl}/data_export/monitoring_anomalies`, {
        email_on_success: emailOnSuccess,
        impersonator_user_id: impersonatorUserId,
      }) as Promise<APIDataExportRequest>;
  }

  initiateSearchHistoryExport({
    emailOnSuccess,
    impersonatorUserId,
    minDate,
    maxDate,
  }: {
    emailOnSuccess: boolean;
    impersonatorUserId: UUID | null;
    minDate: string | null;
    maxDate: string | null;
  }) {
    const params = {
      email_on_success: emailOnSuccess,
      impersonator_user_id: impersonatorUserId,
      min_date: minDate,
      max_date: maxDate,
    };
    return this.authenticated()
      .json()
      .stubbedWith({
        id: "00000000-0000-0000-0000-000000000000",
        retrieval_url: "",
      })
      .post(
        `${this.rootUrl}/data_export/search_history`,
        params
      ) as Promise<APIDataExportRequest>;
  }

  getDataExport({ id }: { id: UUID }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        id: "00000000-0000-0000-0000-000000000000",
        file_url: "https://ocmr.s3.amazonaws.com/ocmr_data_attributes.csv",
        export_state: "SUCCESS",
        export_type: "MonitoringAnomaliesCSV",
        message: null,
        filename: "example.csv",
        export_options: "start date: 2023-01-01, end date: 2023-12-31",
      })
      .get(`${this.rootUrl}/data_export/${id}`) as Promise<APIDataExport>;
  }

  getDataExports() {
    return this.authenticated()
      .json()
      .stubbedWith([
        {
          id: "00000000-0000-0000-0000-000000000000",
          file_url: "https://ocmr.s3.amazonaws.com/ocmr_data_attributes.csv",
          export_state: "SUCCESS",
          export_type: "MonitoringAnomaliesCSV",
          message: null,
          filename: "example.csv",
          export_options: "start date: 2023-01-01, end date: 2023-12-31",
        },
      ])
      .get(`${this.rootUrl}/data_exports/`) as Promise<APIDataExport[]>;
  }

  getRiskDefinitions() {
    return this.authenticated()
      .json()
      .stubbedWith([
        {
          category: "profile",
          subcategory: "application",
          name: "is_submitted_address_virtual",
          display_name: "Virtual submitted address",
          description:
            "The submitted address is a virtual mailbox and not a physical location.",
        },
        {
          category: "website",
          subcategory: "configuration",
          name: "has_scraping_failed",
          display_name: "Web scraping failure",
          description:
            "TrueBiz was unable to scrape content from this website.",
        },
      ])
      .get(`${this.rootUrl}/company/risk_indicator_definitions`) as Promise<
      APIRiskDefinition[]
    >;
  }

  getMonitoringEnrollments({
    limit,
    offset = 0,
    orderBy = [],
    filterBy = [],
    search = "",
  }: {
    limit?: number;
    offset?: number;
    orderBy?: APIMonitoringEnrollmentOrderBy[];
    filterBy?: APIMonitoringEnrollmentFilterBy[];
    search?: string;
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }

            // we need a special empty array value to differentiate from None
            const qsValue = !Array.isArray(value)
              ? value
              : value.length === 0
              ? ["[]"]
              : value;

            if (operator === "in") {
              return [column, qsValue]; // in is now implied
            }

            return [`${column}_${operator}`, qsValue];
          })
        ),
        ...(search ? { search } : {}),
        ...(limit ? { limit } : {}),
        ...(offset ? { offset } : {}),
        ...(orderBy ? { order_by: orderBy } : {}),
      },
      {
        arrayFormat: "none", // repeated keys
        encode: true, // make sure things like emails get % encoded
      }
    );

    type PaginatedEnrollments = APIPaginatedWithPrevAndNextLinks<
      APIMonitoringEnrollment,
      "enrollments"
    >;

    return this.authenticated()
      .json()
      .stubbedWith(monitoringEnrollments)
      .get(
        `${this.rootUrl}/monitoring/_enrollments/?${qs}`
      ) as Promise<PaginatedEnrollments>;
  }

  getMonitoringEnrollmentsMetaUniqueValuesForDomain({
    filterBy = [],
  }: {
    filterBy: APIMonitoringEnrollmentFilterBy[];
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }
            if (operator === "in") {
              return [column, value];
            }
            return [`${column}_${operator}`, value];
          })
        ),
      },
      {
        arrayFormat: "none", // repeated keys,
        encode: true, // make sure things like emails get % encoded
      }
    );
    return this.authenticated()
      .json()
      .stubbedWith([])
      .get(
        `${this.rootUrl}/monitoring/_enrollments/_meta/unique_values_for/domain?${qs}`
      ) as Promise<string[]>;
  }

  getMonitoringEnrollmentsMetaUniqueValuesForExternalRefId({
    filterBy = [],
  }: {
    filterBy: APIMonitoringEnrollmentFilterBy[];
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }
            if (operator === "in") {
              return [column, value];
            }
            return [`${column}_${operator}`, value];
          })
        ),
      },
      {
        arrayFormat: "none", // repeated keys,
        encode: true, // make sure things like emails get % encoded
      }
    );
    return this.authenticated()
      .json()
      .stubbedWith([])
      .get(
        `${this.rootUrl}/monitoring/_enrollments/_meta/unique_values_for/external_ref_id?${qs}`
      ) as Promise<string[]>;
  }

  getMonitoringEnrollmentsMetaUniqueValuesForMonitorTypes({
    filterBy = [],
  }: {
    filterBy: APIMonitoringEnrollmentFilterBy[];
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }
            if (operator === "in") {
              return [column, value];
            }
            return [`${column}_${operator}`, value];
          })
        ),
      },
      {
        arrayFormat: "none", // repeated keys,
        encode: true, // make sure things like emails get % encoded
      }
    );
    return this.authenticated()
      .json()
      .stubbedWith([])
      .get(
        `${this.rootUrl}/monitoring/_enrollments/_meta/unique_values_for/monitor_types?${qs}`
      ) as Promise<APIMonitorType[]>;
  }

  getMonitoringEnrollmentsMetaUniqueValuesForStatus({
    filterBy = [],
  }: {
    filterBy: APIMonitoringEnrollmentFilterBy[];
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }
            if (operator === "in") {
              return [column, value];
            }
            return [`${column}_${operator}`, value];
          })
        ),
      },
      {
        arrayFormat: "none", // repeated keys,
        encode: true, // make sure things like emails get % encoded
      }
    );
    return this.authenticated()
      .json()
      .stubbedWith([])
      .get(
        `${this.rootUrl}/monitoring/_enrollments/_meta/unique_values_for/status?${qs}`
      ) as Promise<APIMonitorStatusType[]>;
  }

  getMonitoringEnrollmentsMetaUniqueValuesForAlerts({
    filterBy = [],
  }: {
    filterBy: APIMonitoringEnrollmentFilterBy[];
  }) {
    const qs = queryString.stringify(
      {
        ...Object.fromEntries(
          filterBy.map(({ column, operator, value }) => {
            if (operator === "eq") {
              return [column, value];
            }
            if (operator === "in") {
              return [column, value];
            }
            return [`${column}_${operator}`, value];
          })
        ),
      },
      {
        arrayFormat: "none", // repeated keys,
        encode: true, // make sure things like emails get % encoded
      }
    );
    return this.authenticated()
      .json()
      .stubbedWith([])
      .get(
        `${this.rootUrl}/monitoring/_enrollments/_meta/unique_values_for/domain?${qs}`
      ) as Promise<string[]>;
  }

  monitoringEnrollmentsMetaBulkAssignToUser(
    monitorDomains: string[],
    assignedToId: UUID | null | undefined
  ) {
    return this.authenticated()
      .json()
      .stubbedWith([])
      .put(
        `${this.rootUrl}/monitoring/_enrollments/_meta/bulk/assign_to_user`,
        {
          monitor_domains: monitorDomains,
          assigned_to_id: assignedToId,
        }
      );
  }

  getMonitoringEnrollment(monitor_domain: string) {
    return this.authenticated()
      .json()
      .stubbedWith(monitoringEnrollment)
      .get(
        `${this.rootUrl}/monitoring/_enrollments/${monitor_domain}/`
      ) as Promise<APIMonitoringEnrollment>;
  }

  unEnrollMonitoringDomain(payload: { domain: string }) {
    return this.authenticated()
      .json()
      .stubbedWith(payload)
      .post(`${this.rootUrl}/monitoring/_enrollments/un_enroll/`, payload, {
        unwrapResponse: false,
      }) as Promise<void>;
  }

  enrollMonitoringDomain(payload: {
    domain: string;
    monitors: APIMonitoringEnrollmentMonitorConfiguration[];
    external_ref_id?: string | null | undefined;
    profile_id?: UUID | null | undefined;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith(payload)
      .post(
        `${this.rootUrl}/monitoring/_enrollments/enroll/`,
        payload
      ) as Promise<APIMonitoringEnrollment>;
  }

  reEnrollMonitoringDomain(payload: {
    domain: string;
    external_ref_id?: string | null | undefined;
    profile_id: UUID | null | undefined;
  }): Promise<APIMonitoringEnrollment> {
    throw new Error("Not implemented");
  }

  batchPauseResumeMonitoringDomain(payload: {
    domains: string[];
    is_enabled: boolean;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith(payload)
      .put(
        `${this.rootUrl}/monitoring/_enrollments/_meta/bulk/pause_resume`,
        payload
      ) as Promise<{ is_enabled: boolean; domains: string[] }>;
  }

  setMonitorEnabled(payload: {
    domain: string;
    monitor_type: APIMonitorType;
    is_enabled: boolean;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith(payload)
      .post(
        `${this.rootUrl}/monitoring/_enrollments/${payload.domain}/configure_monitors`,
        {
          monitors: [
            { type: payload.monitor_type, is_enabled: payload.is_enabled },
          ],
        }
      ) as Promise<APIMonitoringEnrollment>;
  }

  addAnomalyComment(anomalyId: UUID, message: string) {
    return this.authenticated()
      .json()
      .stubbedWith({})
      .post(`${this.rootUrl}/monitoring/_anomalies/${anomalyId}/add_comment`, {
        message,
      }) as Promise<APIMonitoringAnomaly>;
  }

  assignAnomalyToUser(anomalyId: UUID, assignedToId: UUID | null | undefined) {
    return this.authenticated()
      .json()
      .stubbedWith({})
      .post(
        `${this.rootUrl}/monitoring/_anomalies/${anomalyId}/assign_to_user`,
        {
          assigned_to_id: assignedToId || null,
        }
      ) as Promise<APIMonitoringAnomaly>;
  }

  resolveAnomaly(
    anomalyId: UUID,
    {
      resolutionType,
      resolutionDescription,
    }: {
      resolutionType: APIAnomalyResolutionType;
      resolutionDescription: string;
    }
  ) {
    return this.authenticated()
      .json()
      .stubbedWith({})
      .post(`${this.rootUrl}/monitoring/_anomalies/${anomalyId}/resolve`, {
        resolution_type: resolutionType,
        resolution_description: resolutionDescription,
      }) as Promise<APIMonitoringAnomaly>;
  }

  getCompanyMatchProfiles() {
    return this.authenticated()
      .json()
      .stubbedWith(companyMatchProfiles)
      .get(`${this.rootUrl}/company/match_profiles`)
      .then(async (profiles: APICompanyMatchProfile[]) => {
        return profiles.filter((profile) => profile.status !== "deleted");
      }) as Promise<APICompanyMatchProfile[]>;
  }

  getDeletedCompanyMatchProfiles() {
    return this.authenticated()
      .json()
      .stubbedWith(companyMatchProfiles)
      .get(`${this.rootUrl}/company/match_profiles`)
      .then(async (profiles: APICompanyMatchProfile[]) => {
        return profiles.filter((profile) => profile.status === "deleted");
      }) as Promise<APICompanyMatchProfile[]>;
  }

  getCompanyMatchProfileConstants() {
    return this.authenticated()
      .json()
      .stubbedWith(matchProfileConstants)
      .get(
        `${this.rootUrl}/company/match_profile_constants`
      ) as Promise<APICompanyMatchProfileConstants>;
  }

  async createMatchProfile(matchProfile: APICompanyMatchProfile) {
    try {
      const matchProfileResponse = (await this.authenticated()
        .json()
        .stubbedWith(createMatchProfileResponse)
        .post(
          `${this.rootUrl}/company/match_profiles`,
          matchProfile
        )) as APICompanyMatchProfile;

      return matchProfileResponse;
    } catch (e) {
      let apiResponse: any;
      if (this.isResponseObjectError(e)) {
        apiResponse = await e.json();
      } else {
        apiResponse = JSON.stringify(e);
      }

      Sentry.captureMessage(
        "[createMatchProfile]: error creating match profile",
        {
          level: "error",
          extra: {
            userInput: matchProfile,
            apiResponse,
            // sentry does not handle objects more than 2 levels deep
            // so also include string representations
            userInputJSON: JSON.stringify(matchProfile),
            apiResponseJSON: JSON.stringify(apiResponse),
          },
        }
      );

      throw e;
    }
  }

  deleteCompanyMatchProfile(companyMatchProfileId: string) {
    return this.authenticated()
      .json()
      .stubbedWith({
        message: `Delete companyMatchProfile ${companyMatchProfileId}.`,
      })
      .delete(`${this.rootUrl}/company/match_profile/${companyMatchProfileId}`);
  }

  setDefaultCompanyMatchProfile(companyMatchProfileId: string | null) {
    return this.authenticated()
      .json()
      .stubbedWith({
        message: "updated account default profile",
      })
      .post(`${this.rootUrl}/company/set_match_profile_default/`, {
        match_profile_id: companyMatchProfileId,
      });
  }

  getWebsiteStatus(website: string) {
    return this.authenticated()
      .json()
      .stubbedWith(websiteStatusResponse)
      .post(`${this.rootUrl}/website/status`, {
        website: website,
        request_timeout: 7.5,
      });
  }

  getAccountSearchStats(
    minDateTime?: string,
    maxDateTime?: string
  ): Promise<APIAccountSearchSummary> {
    const searchParams = new URLSearchParams();

    if (minDateTime) {
      searchParams.set("min_datetime", minDateTime);
    }

    if (maxDateTime) {
      searchParams.set("max_datetime", maxDateTime);
    }

    const search = searchParams.toString();

    return this.authenticated()
      .json()
      .stubbedWith(accountSearchStats)
      .get(`${this.rootUrl}/stats/searches${search ? `?${search}` : ""}`);
  }

  startAssistantChatThread({
    subjectId,
    subjectType,
  }: {
    subjectId: UUID;
    subjectType: "WPR";
  }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        thread_id: window.crypto.randomUUID(),
        messages_url: `${this.rootUrl}/threads/thread_abc123/messages`,
      })
      .post(`${this.rootUrl}/assistants/threads`, {
        subject_id: subjectId,
        subject_type: subjectType,
      }) as Promise<{ thread_id: UUID; messages_url: string }>;
  }

  postAssistantChatThreadMessage({
    content,
    threadId,
  }: {
    content: string;
    threadId: UUID;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        id: window.crypto.randomUUID(),
        thread_id: window.crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: "processing",
        role: "assistant",
        author: "foo@truebiz.io",
        content: [
          {
            type: "text",
            text: {
              value: "A message",
              annotations: [],
            },
          },
        ],
      })
      .post(`${this.rootUrl}/assistants/threads/${threadId}/messages`, {
        content,
      }) as Promise<{
      id: UUID;
      thread_id: UUID;
      created_at: ISODateTime;
      updated_at: ISODateTime;
      role: "user" | "assistant";
      status: "processing" | "finished" | "error";
      author: string;
      content: {
        type: "text";
        text: {
          value: string;
          annotations: any[];
        };
      }[];
    }>;
  }

  getAssistantChatThreadMessage({
    threadId,
    messageId,
  }: {
    threadId: UUID;
    messageId: UUID;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        id: window.crypto.randomUUID(),
        thread_id: window.crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: "processing",
        role: "assistant",
        author: "foo@truebiz.io",
        content: [
          {
            type: "text",
            text: {
              value: "A message",
              annotations: [],
            },
          },
        ],
      })
      .get(
        `${this.rootUrl}/assistants/threads/${threadId}/messages/${messageId}`
      ) as Promise<{
      id: UUID;
      thread_id: UUID;
      created_at: ISODateTime;
      updated_at: ISODateTime;
      role: "user" | "assistant";
      status: "processing" | "finished" | "error";
      author: string;
      content: {
        type: "text";
        text: {
          value: string;
          annotations: any[];
        };
      }[];
    }>;
  }

  submitBusinessSearch({
    submittedBusinessName,
    submittedWebsite,
    city,
    stateProvince,
    postalCode,
    country,
  }: {
    submittedBusinessName: string;
    submittedWebsite?: string | null;
    city: string;
    stateProvince?: string | null;
    postalCode?: string | null;
    country: string;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: "PROCESSING",
        status_description: null,
        result: null,
        query: null,
      } as APIBusinessSearch)
      .post(`${this.rootUrl}/industry-searches`, {
        submitted_business_name: submittedBusinessName,
        submitted_website: submittedWebsite,
        city,
        state_province: stateProvince,
        postal_code: postalCode,
        country,
      }) as Promise<APIBusinessSearch>;
  }

  getBusinessSearch({ searchId }: { searchId: UUID }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: "PROCESSING",
        status_description: null,
        result: null,
        query: null,
      } as APIBusinessSearch)
      .get(
        `${this.rootUrl}/industry-searches/${searchId}`
      ) as Promise<APIBusinessSearch>;
  }

  submitFraudSearch({
    businessName,
    website,
    city,
    stateProvince,
    postalCode,
    country,
  }: {
    businessName: string;
    website?: string | null;
    city: string;
    stateProvince?: string | null;
    postalCode?: string | null;
    country: string;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: "PROCESSING",
        status_description: null,
        result: null,
        query: null,
      } as APIFraudSearch)
      .post(`${this.rootUrl}/fraud-searches`, {
        business_name: businessName,
        website,
        city,
        state_province: stateProvince,
        postal_code: postalCode,
        country,
      }) as Promise<APIFraudSearch>;
  }

  getFraudSearch({ searchId }: { searchId: UUID }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: "PROCESSING",
        status_description: null,
        result: null,
        query: null,
      } as APIFraudSearch)
      .get(
        `${this.rootUrl}/fraud-searches/${searchId}`
      ) as Promise<APIFraudSearch>;
  }

  listFraudSearches({ limit, offset }: { limit?: number; offset?: number }) {
    const qs = queryString.stringify({
      ...(limit ? { limit } : {}),
      ...(offset ? { offset } : {}),
    });

    return this.authenticated()
      .json()
      .stubbedWith({
        limit: 0,
        offset: 0,
        count: 0,
        searches: [],
      } as APIPaginated<APIFraudSearch, "searches">)
      .get(`${this.rootUrl}/fraud-searches` + (qs ? `?${qs}` : "")) as Promise<
      APIPaginated<APIFraudSearch, "searches">
    >;
  }

  deepSearch({
    businessName,
    website,
    location,
  }: {
    businessName?: string | null;
    website?: string | null;
    location?: string | null;
  }) {
    return this.authenticated()
      .json()
      .stubbedWith({
        message: `Request for ${businessName || website} processing.`,
        tracking_id: crypto.randomUUID(),
      } as APIDeepSearch)
      .post(`${this.rootUrl}/company/deep-search`, {
        submitted_business_name: businessName || website,
        submitted_website: website,
        country: location || "global",
      }) as Promise<APIDeepSearch>;
  }
}
